import { useAuth } from '@/domain/context/auth';
import { DOCUMENTS_DB } from '@/domain/env/documents-db';
import { AudienceFormData } from '@/domain/types/audience';
import { useAdminLogger } from '@/domain/use-cases/admin-logger';
import {
  createAudience,
  listAudiences,
  updateAudience,
} from '@/domain/use-cases/audiences/audience-service';
import Button from '@/presentation/components/button';
import { useEffect, useState } from 'react';
import { FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import { useNavigate, useSearchParams } from 'react-router-dom';

const NAME_REQUIRED = 'El nombre es obligatorio';

const labelClass = 'mb-1 block text-sm font-medium text-gray-800';
const inputClass =
  'h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-gray-900 placeholder-gray-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary';

const defaultForm: AudienceFormData = {
  name: '',
  nameEN: '',
  description: '',
  isActive: true,
};

const AudienceAddPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const { user } = useAuth();
  const { logCreate, logUpdate } = useAdminLogger();
  const [form, setForm] = useState<AudienceFormData>(defaultForm);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editId) return;
    listAudiences().then((audiences) => {
      const audience = audiences.find((a) => a.id === editId);
      if (!audience) return;
      setForm({
        name: audience.name,
        nameEN: audience.nameEN ?? '',
        description: audience.description ?? '',
        isActive: audience.isActive,
      });
    });
  }, [editId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError(NAME_REQUIRED);
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      if (editId) {
        await updateAudience(editId, form);
        await logUpdate(DOCUMENTS_DB.AUDIENCES, editId, form.name, form);
      } else {
        const newId = await createAudience(form, user?.email ?? '');
        await logCreate(DOCUMENTS_DB.AUDIENCES, newId, form.name, form);
      }
      navigate('/dashboard/audiences');
    } catch {
      setError('Error al guardar la audiencia');
    } finally {
      setIsSaving(false);
    }
  };

  const goBack = () => navigate('/dashboard/audiences');

  return (
    <div className="flex flex-col gap-5 p-4 max-w-2xl">
      <div>
        <button
          type="button"
          onClick={goBack}
          className="mb-3 inline-flex items-center gap-1.5 rounded text-sm text-gray-600 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <FiArrowLeft aria-hidden="true" />
          Volver a audiencias
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {editId ? 'Editar audiencia' : 'Nueva audiencia'}
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          {editId
            ? 'Actualiza los datos de este grupo de usuarios.'
            : 'Crea un grupo de usuarios para dirigirle publicaciones específicas.'}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
      >
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label htmlFor="audience-name" className={labelClass}>
              Nombre <span className="text-primary">*</span>
            </label>
            <input
              id="audience-name"
              name="name"
              value={form.name}
              onChange={handleChange}
              className={inputClass}
              placeholder="Empleados Bogotá"
              autoFocus={!editId}
              aria-required="true"
              aria-invalid={error === NAME_REQUIRED}
            />
          </div>

          <div>
            <label htmlFor="audience-name-en" className={labelClass}>
              Name (English)
            </label>
            <input
              id="audience-name-en"
              name="nameEN"
              value={form.nameEN}
              onChange={handleChange}
              className={inputClass}
              placeholder="Bogotá Employees"
            />
          </div>
        </div>

        <div>
          <label htmlFor="audience-description" className={labelClass}>
            Descripción
          </label>
          <textarea
            id="audience-description"
            name="description"
            value={form.description}
            onChange={handleChange}
            className={`${inputClass} h-auto py-2 resize-y`}
            rows={3}
            placeholder="Describe a quién pertenece este grupo..."
          />
          <p className="mt-1 text-xs text-gray-500">
            Nota interna para distinguir audiencias parecidas.
          </p>
        </div>

        <label
          htmlFor="audience-active"
          className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
        >
          <span>
            <span className="block text-sm font-medium text-gray-900">
              Audiencia activa
            </span>
            <span className="block text-xs text-gray-600">
              Las audiencias inactivas no se pueden elegir al editar un usuario
              ni al crear una publicación.
            </span>
          </span>
          <input
            id="audience-active"
            type="checkbox"
            name="isActive"
            checked={form.isActive}
            onChange={handleChange}
            className="sr-only"
          />
          <span
            aria-hidden="true"
            className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 ${
              form.isActive ? 'bg-primary' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                form.isActive ? 'translate-x-5' : 'translate-x-0.5'
              }`}
            />
          </span>
        </label>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
          >
            <FiAlertCircle
              className="mt-0.5 flex-shrink-0"
              aria-hidden="true"
            />
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-200 pt-5 sm:flex-row">
          <Button
            type="submit"
            label={editId ? 'Guardar cambios' : 'Crear audiencia'}
            isLoading={isSaving}
            className="w-full sm:w-auto sm:px-8"
          />
          <button
            type="button"
            onClick={goBack}
            className="h-12 w-full rounded border border-gray-300 bg-white px-6 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary sm:w-auto"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default AudienceAddPage;
